import { useEffect } from "react";

/**
 * The custom events should be named using the following format: "on:<event-name>"
 * By using "on:" at the beginning of the event name, we can easily identify we are listening to a custom event.
 */
export type Events = {
  /**
   * Event triggered when the user submits a form or action.
   * @type {{target: string, data?: any}}
   */
  "on:submit": {
    /**
     * The target is used to identify the component or view that will be triggered by the event.
     * For example, "package-creation".
     */
    target: string;
    /**
     * Optional. The data that will be passed to the component or view triggered by the event.
     */
    data?: any;
  };
  /**
   * Event triggered when the user wants to update the data.
   * @type {{target: string, data?: any}}
   */
  "on:update": {
    /**
     * The target is used to identify the component or view that will be triggered by the event.
     * For example, "group-referent".
     */
    target: string;
    /**
     * Optional. The data that will be passed to the component or view triggered by the event.
     * For example, { linkId: "123", groupId: "456", isReferent: true }.
     */
    data?: any;
  };
  /**
   * Event triggered when the user wants to refresh the data.
   * @type {{target: string}}
   */
  "on:refresh": {
    /**
     * The target is used to identify the component or view that will be triggered by the event.
     * For example, "group-calendar".
     */
    target: string;
  };
  /** Event triggered to navigate to a specific tab or view. */
  "on:navigate": {
    /**
     * The target is used to identify the tab or view to navigate to.
     * For example, "assignment-tab".
     */
    target: string;
  };
};

interface UseListenEventProps {
  eventKey: keyof Events;
  handler: (detail: Events[keyof Events]) => void;
}

const bus = new EventTarget();

/**
 * Emit a custom event.
 * @param eventKey - The key of the event to emit.
 * @param detail - The detail of the event to emit.
 */
export const emitEvent = <K extends keyof Events>(eventKey: K, detail: Events[K]) => {
  bus.dispatchEvent(new CustomEvent(eventKey, { detail }));
};

/**
 * A custom React hook that listens to a custom event and calls a handler function when the event is triggered.
 * @param props.eventKey - The key of the event to listen to.
 * @param props.handler - The handler function to call when the event is triggered.
 */
export const useListenEvent = <K extends keyof Events>({
  eventKey,
  handler,
}: UseListenEventProps) => {
  useEffect(() => {
    const eventWrapper = (e: Event) => {
      handler((e as CustomEvent<Events[K]>)?.detail);
    };

    bus.addEventListener(eventKey, eventWrapper);

    return () => {
      bus.removeEventListener(eventKey, eventWrapper);
    };
  }, [eventKey, handler]);
};
